// Edição do problema: enunciado com prévia e casos de teste.
//
// Mexer em caso de teste depois que a prova começou invalida julgamentos já
// feitos — a tela avisa e oferece o rejulgamento logo em seguida.

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Check, FlaskConical, Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import type { AdminProblemDto, AdminTestCaseDto, ContestStateDto } from "@compila/shared";
import { api } from "../../lib/api.js";
import { Button } from "../../components/motion/button/base";
import { Input } from "../../components/motion/input";
import { Checkbox } from "../../components/motion/checkbox";
import { AlertMessage, useAlertConfirm } from "../../components/alerts";
import { EmptyState, Loading } from "../../components/domain.js";

const fields = {
  field: "rounded-xl bg-background",
  label: "px-0",
  input: "placeholder:text-muted-foreground",
};

const textareaClass =
  "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none transition-colors focus-visible:border-accent";

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Não foi possível concluir a operação.";

export function AdminProblemEditPage(): React.JSX.Element {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();

  const problems = useQuery({
    queryKey: ["admin-problems"],
    queryFn: () => api.get<AdminProblemDto[]>("/admin/problems"),
  });

  const contest = useQuery({
    queryKey: ["contest"],
    queryFn: () => api.get<ContestStateDto>("/contest"),
  });

  const problem = problems.data?.find((p) => p.id === id);

  const [statement, setStatement] = useState("");
  const [title, setTitle] = useState("");

  useEffect(() => {
    if (problem) {
      setStatement(problem.statementMd);
      setTitle(problem.title);
    }
  }, [problem?.id]);

  const save = useMutation({
    mutationFn: () =>
      api.patch<AdminProblemDto>(`/admin/problems/${id}`, {
        title: title.trim(),
        statementMd: statement,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-problems"] }),
  });

  if (problems.isPending) return <Loading label="Carregando o problema…" />;
  if (!problem) {
    return (
      <div className="space-y-4">
        <AlertMessage tone="error">Problema não encontrado.</AlertMessage>
        <Link to="/admin/problemas" className="text-sm text-accent hover:underline">
          Voltar para os problemas
        </Link>
      </div>
    );
  }

  const changed = title.trim() !== problem.title || statement !== problem.statementMd;

  return (
    <div className="space-y-7">
      <div>
        <Link
          to="/admin/problemas"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft size={15} aria-hidden />
          Problemas
        </Link>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="grid size-10 place-items-center rounded-xl border border-border bg-card text-lg font-semibold">
            {problem.label}
          </span>
          <h1 className="text-[28px] font-semibold tracking-tight">{problem.title}</h1>
          {!problem.isVisible && (
            <span className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground">
              oculto para as equipes
            </span>
          )}
        </div>
      </div>

      <section className="rounded-2xl border border-border bg-card p-6">
        <h2 className="text-sm font-medium">Enunciado</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Markdown à esquerda, prévia à direita — é assim que a equipe vai ler.
        </p>

        <div className="mt-5">
          <Input
            id="problema-titulo"
            label="Título"
            value={title}
            onChange={setTitle}
            disabled={save.isPending}
            classNames={fields}
          />
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-sm">Markdown</span>
            <textarea
              className={`${textareaClass} h-96 font-mono text-xs`}
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              disabled={save.isPending}
            />
          </label>
          <div>
            <span className="mb-1.5 block text-sm">Prévia</span>
            <div className="prose prose-invert prose-sm h-96 max-w-none overflow-auto rounded-xl border border-border bg-background p-4">
              <Markdown remarkPlugins={[remarkGfm]}>{statement}</Markdown>
            </div>
          </div>
        </div>

        {save.error && (
          <div className="mt-4">
            <AlertMessage tone="error">{errorMessage(save.error)}</AlertMessage>
          </div>
        )}

        <div className="mt-5 flex items-center gap-3">
          <Button
            className="rounded-xl"
            disabled={save.isPending || !changed}
            onClick={() => save.mutate()}
          >
            <Save size={15} aria-hidden />
            {save.isPending ? "Salvando…" : "Salvar enunciado"}
          </Button>
          {save.isSuccess && !changed && (
            <span
              role="status"
              className="inline-flex items-center gap-1.5 text-sm text-accent"
            >
              <Check size={15} aria-hidden />
              Salvo
            </span>
          )}
        </div>
      </section>

      <TestCases problemId={id} contestStatus={contest.data?.status ?? "DRAFT"} />
    </div>
  );
}

function TestCases({
  problemId,
  contestStatus,
}: {
  problemId: string;
  contestStatus: ContestStateDto["status"];
}) {
  const confirmAlert = useAlertConfirm();
  const queryClient = useQueryClient();
  const running = contestStatus !== "DRAFT";

  const cases = useQuery({
    queryKey: ["test-cases", problemId],
    queryFn: () => api.get<AdminTestCaseDto[]>(`/admin/problems/${problemId}/test-cases`),
  });

  const reload = () =>
    queryClient.invalidateQueries({ queryKey: ["test-cases", problemId] });

  const [stdin, setStdin] = useState("");
  const [expected, setExpected] = useState("");
  const [isSample, setIsSample] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      api.post<AdminTestCaseDto>(`/admin/problems/${problemId}/test-cases`, {
        position: (cases.data?.length ?? 0) + 1,
        stdin,
        expectedOutput: expected,
        isSample,
      }),
    onSuccess: () => {
      setStdin("");
      setExpected("");
      setIsSample(false);
      void reload();
      void queryClient.invalidateQueries({ queryKey: ["admin-problems"] });
    },
  });

  const remove = useMutation({
    mutationFn: (testCaseId: string) =>
      api.delete(`/admin/problems/${problemId}/test-cases/${testCaseId}`, {
        confirm: true,
      }),
    onSuccess: () => {
      void reload();
      void queryClient.invalidateQueries({ queryKey: ["admin-problems"] });
    },
  });

  const rejudge = useMutation({
    mutationFn: () =>
      api.post<{ count: number }>(`/admin/problems/${problemId}/rejudge`, {
        confirm: true,
      }),
  });

  if (cases.isPending) return <Loading label="Carregando os casos de teste…" />;

  const list = cases.data ?? [];

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-medium">
            <FlaskConical size={16} className="text-muted-foreground" aria-hidden />
            Casos de teste ({list.length})
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Casos marcados como exemplo aparecem no enunciado. Os demais nunca são
            mostrados às equipes.
          </p>
        </div>

        {running && (
          <Button
            variant="secondary"
            className="rounded-xl"
            disabled={rejudge.isPending}
            onClick={async () => {
              if (
                await confirmAlert(
                  "Rejulgar TODAS as submissões deste problema? O placar de todas as equipes será recalculado.",
                )
              ) {
                rejudge.mutate();
              }
            }}
          >
            <RefreshCw
              size={15}
              className={rejudge.isPending ? "animate-spin" : ""}
              aria-hidden
            />
            {rejudge.isPending ? "Rejulgando…" : "Rejulgar o problema"}
          </Button>
        )}
      </div>

      <div className="mt-5 space-y-4">
        {running && (
          <AlertMessage tone="warning">
            A prova já começou. Alterar ou remover um caso invalida os julgamentos já
            feitos — rejulgue o problema logo depois.
          </AlertMessage>
        )}

        {rejudge.isSuccess && (
          <AlertMessage>
            {rejudge.data.count} submissões voltaram para a fila. Contam como pendentes no
            placar até o novo veredito.
          </AlertMessage>
        )}

        {(remove.error || create.error) && (
          <AlertMessage tone="error">
            {errorMessage(remove.error ?? create.error)}
          </AlertMessage>
        )}

        {list.length === 0 ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4">
            <EmptyState
              icon={FlaskConical}
              title="Sem casos de teste"
              description="Qualquer submissão a este problema vira falha do juiz. Adicione ao menos um caso antes de liberá-lo."
            />
          </div>
        ) : (
          <ul className="space-y-2">
            {list.map((c) => (
              <li
                key={c.id}
                className="grid grid-cols-[48px_1fr_1fr_auto] items-start gap-3 rounded-xl border border-border bg-background p-3"
              >
                <span className="pt-2 font-mono text-sm text-muted-foreground">
                  #{c.position}
                </span>
                <pre className="max-h-24 overflow-auto rounded-lg bg-card p-2 text-xs">
                  <code>{c.stdin}</code>
                </pre>
                <pre className="max-h-24 overflow-auto rounded-lg bg-card p-2 text-xs">
                  <code>{c.expectedOutput}</code>
                </pre>
                <div className="flex items-center gap-2">
                  {c.isSample ? (
                    <span className="rounded-full border border-accent/40 px-2 py-0.5 text-[11px] text-accent">
                      exemplo
                    </span>
                  ) : (
                    <span className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
                      oculto
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remover o caso ${c.position}`}
                    disabled={remove.isPending}
                    onClick={async () => {
                      if (await confirmAlert(`Remover o caso de teste #${c.position}?`)) {
                        remove.mutate(c.id);
                      }
                    }}
                  >
                    <Trash2 size={15} className="text-destructive" aria-hidden />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="rounded-xl border border-border bg-background p-5">
          <h3 className="flex items-center gap-2 text-sm font-medium">
            <Plus size={15} className="text-muted-foreground" aria-hidden />
            Adicionar caso
          </h3>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-sm">Entrada (stdin)</span>
              <textarea
                className={`${textareaClass} h-28 font-mono text-xs`}
                value={stdin}
                onChange={(e) => setStdin(e.target.value)}
                disabled={create.isPending}
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm">Saída esperada</span>
              <textarea
                className={`${textareaClass} h-28 font-mono text-xs`}
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
                disabled={create.isPending}
              />
            </label>
          </div>

          <label className="mt-4 flex items-center gap-2.5 text-sm">
            <Checkbox
              checked={isSample}
              onCheckedChange={(v: boolean) => setIsSample(v)}
              disabled={create.isPending}
            />
            Mostrar como exemplo no enunciado
          </label>

          <Button
            className="mt-4 rounded-xl"
            disabled={create.isPending || expected.length === 0}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Adicionando…" : "Adicionar caso"}
          </Button>
        </div>
      </div>
    </section>
  );
}
